import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Section from '../components/Section';
import HeaderBar from '../components/HeaderBar';
import AdherenceChart from '../components/AdherenceChart';
import AdherenceHeatmap from '../components/AdherenceHeatmap';
import { adherenceService, AdherenceStatistic } from '../services/adherenceService';
import Tabs from '../components/Tabs';
import EditPatientModal from '../components/EditPatientModal';
import PatientProfileHeader from '../components/PatientProfileHeader';
import PlanPanel from '../components/PlanPanel';
import DocumentManager from '../components/DocumentManager';
import ConfirmModal from '../components/ConfirmModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { patientsService, Patient, UpdatePatientData } from '../services/patientsService';
import { remindersService } from '../services/remindersService';
import { Reminder, CreateSystemReminderData, UpdateReminderData } from '../models/reminder';
import ReminderCard from '../components/ReminderCard';
import ReminderModal from '../components/ReminderModal';
import AgentConfigPanel from '../components/AgentConfigPanel';
import { AgentConfig } from '../services/agentService';
import AgentStatusDot, { computeAgentDotState } from '../components/AgentStatusDot';
import SleepPanel from '../components/SleepPanel';
import InsightsPanel from '../components/insights/InsightsPanel';
import PatientInfoDetails from '../components/PatientInfoDetails';
import { useIsMobile } from '../hooks/useIsMobile';

// Tab slugs map 1:1 to the tab order rendered below. Used to associate each
// tab with a `?tab=<slug>` URL param so tabs are deep-linkable.
const TAB_SLUGS = [
  'estadisticas',
  'insights',
  'plan',
  'documentos',
  'sueno',
  'recordatorios',
  'agente',
  'perfil',
] as const;



const PatientDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [editPatientModalOpen, setEditPatientModalOpen] = useState(false);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);

  // Selected tab is derived from the `?tab=<slug>` URL param so tabs are
  // deep-linkable. Unknown/missing slug falls back to the first tab.
  const tabFromUrl = TAB_SLUGS.indexOf((searchParams.get('tab') || '') as typeof TAB_SLUGS[number]);
  const selectedTab = tabFromUrl === -1 ? 0 : tabFromUrl;
  const handleSelectTab = (idx: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', TAB_SLUGS[idx]);
        return next;
      },
      { replace: true }
    );
  };
  const [adherenceData, setAdherenceData] = useState<AdherenceStatistic[]>([]);
  const [adherenceLoading, setAdherenceLoading] = useState(false);
  const [adherenceError, setAdherenceError] = useState<string | null>(null);
  const [heatmapData, setHeatmapData] = useState<AdherenceStatistic[]>([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapError, setHeatmapError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [remindersError, setRemindersError] = useState<string | null>(null);
  const [createReminderModalOpen, setCreateReminderModalOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [editReminderModalOpen, setEditReminderModalOpen] = useState(false);
  const [agentConfig, setAgentConfig] = useState<AgentConfig | null>(null);

  // Fetch patient data when component mounts or ID changes
  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const patientData = await patientsService.getPatientById(id);
        setPatient(patientData);
      } catch (err) {
        console.error('Error fetching patient:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  // Fetch adherence data (line chart — last 10 weeks)
  useEffect(() => {
    const fetchAdherenceData = async () => {
      if (!patient?.userId) return;

      try {
        setAdherenceLoading(true);
        setAdherenceError(null);
        const adherenceStats = await adherenceService.getLastTenWeeksAdherence(patient.userId);
        setAdherenceData(adherenceStats);
      } catch (err) {
        setAdherenceError('Failed to load adherence data');
        console.error('Error fetching adherence data:', err);
        setAdherenceData([]);
      } finally {
        setAdherenceLoading(false);
      }
    };

    fetchAdherenceData();
  }, [patient?.userId]);

  // Fetch adherence data (heatmap — last 52 weeks)
  useEffect(() => {
    const fetchHeatmapData = async () => {
      if (!patient?.userId) return;

      try {
        setHeatmapLoading(true);
        setHeatmapError(null);
        const stats = await adherenceService.getYearAdherence(patient.userId);
        setHeatmapData(stats);
      } catch (err) {
        setHeatmapError('Failed to load heatmap data');
        console.error('Error fetching heatmap data:', err);
        setHeatmapData([]);
      } finally {
        setHeatmapLoading(false);
      }
    };

    fetchHeatmapData();
  }, [patient?.userId]);


  // Fetch reminders when patient is loaded
  useEffect(() => {
    const fetchReminders = async () => {
      if (!id) return;

      try {
        setRemindersLoading(true);
        setRemindersError(null);
        const data = await remindersService.getReminders(id);
        setReminders(data);
      } catch (err) {
        setRemindersError('Error al cargar los recordatorios');
        console.error('Error fetching reminders:', err);
        setReminders([]);
      } finally {
        setRemindersLoading(false);
      }
    };

    fetchReminders();
  }, [id]);

  // Reminder handlers
  const handleCreateReminder = async (data: CreateSystemReminderData | UpdateReminderData) => {
    if (!id) return;
    await remindersService.createReminder(id, data as CreateSystemReminderData);
    const updated = await remindersService.getReminders(id);
    setReminders(updated);
    setCreateReminderModalOpen(false);
  };

  const handleUpdateReminder = async (data: UpdateReminderData) => {
    if (!id || !editingReminder) return;
    await remindersService.updateReminder(id, editingReminder.id, data);
    const updated = await remindersService.getReminders(id);
    setReminders(updated);
    setEditReminderModalOpen(false);
    setEditingReminder(null);
  };

  const handleDeleteReminder = async (reminder: Reminder) => {
    if (!id) return;
    try {
      await remindersService.deleteReminder(id, reminder.id);
      setReminders(prev => prev.filter(r => r.id !== reminder.id));
    } catch (err) {
      console.error('Error deleting reminder:', err);
      alert('Error al eliminar el recordatorio. Por favor, inténtalo de nuevo.');
    }
  };

  const handleToggleReminderEnabled = async (reminder: Reminder) => {
    if (!id) return;
    const updated = await remindersService.updateReminder(id, reminder.id, { enabled: !reminder.enabled });
    setReminders(prev => prev.map(r => r.id === updated.id ? updated : r));
  };

  // Handle patient update
  const handleUpdatePatient = async (data: Partial<UpdatePatientData>) => {
    if (!id) return;
    
    try {
      await patientsService.updatePatient(id, data);
      setEditPatientModalOpen(false);
      // Refetch patient data from API
      const patientData = await patientsService.getPatientById(id);
      setPatient(patientData);
    } catch (error) {
      console.error('Failed to update patient:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  // Handle patient deactivation
  const handleDeletePatient = async () => {
    if (!patient?.userId) return;
    
    try {
      await patientsService.deactivatePatient(patient.userId);
      setDeleteConfirmOpen(false);
      // Navigate back to the patients list after successful deactivation
      navigate('/app');
    } catch (error) {
      console.error('Failed to deactivate patient:', error);
      alert('Error al desactivar el paciente. Por favor, inténtalo de nuevo.');
    }
  };

  // Handle patient password change
  const handleChangePassword = async (password: string) => {
    if (!id) return;
    await patientsService.updatePatientPassword(id, password);
    setChangePasswordOpen(false);
  };

  if (loading) {
    return (
      <div style={{ padding: '0' }}>
        <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app/patients')} />
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#666' }}>Cargando datos del paciente...</div>
        </div>
      </div>
    );
  }


  if (!patient) {
    return (
      <div style={{ padding: '0' }}>
        <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app/patients')} />
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#666' }}>Paciente no encontrado.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app/patients')} />
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: isMobile ? '0 16px' : 0 }}>
        <PatientProfileHeader
          patient={patient}
          onEditData={() => setEditPatientModalOpen(true)}
          onChangePassword={() => setChangePasswordOpen(true)}
          onDelete={() => setDeleteConfirmOpen(true)}
        />
        {/* Tabs + content (full width) */}
        <div style={{ minWidth: 0 }}>
            <Tabs
              tabs={[
                "Estadísticas",
                "Insights",
                "Plan",
                "Documentos",
                "Sueño",
                "Recordatorios",
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  Agente
                  {agentConfig
                    ? <AgentStatusDot state={computeAgentDotState(agentConfig)} size={9} />
                    : patient.serviceStatus
                      ? <AgentStatusDot
                          state={computeAgentDotState({
                            enabled: patient.serviceStatus.agentEnabled,
                            proactive_enabled: patient.serviceStatus.agentProactiveEnabled,
                          })}
                          size={9}
                        />
                      : null}
                </span>,
                "Perfil",
              ]}
              selected={selectedTab}
              onSelect={handleSelectTab}
            />
            {selectedTab === 0 && (
              <>
                <Section>
                  <div style={{ fontWeight: 700, marginBottom: 8, color: '#222' }}>Grafica Adherencia al plan</div>
                  <AdherenceChart 
                    data={adherenceData}
                    loading={adherenceLoading}
                    error={adherenceError}
                  />
                </Section>
                <Section>
                  <div style={{ fontWeight: 700, marginBottom: 12, color: '#222' }}>Calendario de Adherencia</div>
                  <AdherenceHeatmap
                    data={heatmapData}
                    loading={heatmapLoading}
                    error={heatmapError}
                  />
                </Section>
              </>
            )}
            {selectedTab === 1 && patient && (
              <InsightsPanel patientId={id || ''} />
            )}
            {selectedTab === 2 && patient && (
              <PlanPanel patientId={id || ''} patientUserId={patient.userId} />
            )}
            {selectedTab === 3 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <DocumentManager patientId={id || ''} />
              </Section>
            )}
            {selectedTab === 4 && patient && (
              <SleepPanel patientUserId={patient.userId} />
            )}
            {selectedTab === 5 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#222' }}>Recordatorios del paciente</div>
                  <button
                    onClick={() => setCreateReminderModalOpen(true)}
                    style={{
                      background: '#38b2ac',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 16px',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#319795'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#38b2ac'; }}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                    <span>Añadir Recordatorio</span>
                  </button>
                </div>
                {remindersLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    Cargando recordatorios...
                  </div>
                ) : remindersError ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#e53e3e' }}>
                    {remindersError}
                  </div>
                ) : reminders.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    No hay recordatorios configurados para este paciente.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {reminders.map((reminder) => (
                      <ReminderCard
                        key={reminder.id}
                        reminder={reminder}
                        patientId={id || ''}
                        onToggleEnabled={handleToggleReminderEnabled}
                        onEdit={(r) => { setEditingReminder(r); setEditReminderModalOpen(true); }}
                        onDelete={handleDeleteReminder}
                      />
                    ))}
                  </div>
                )}
                <ReminderModal
                  open={createReminderModalOpen}
                  mode="create"
                  onSave={handleCreateReminder}
                  onCancel={() => setCreateReminderModalOpen(false)}
                />
                <ReminderModal
                  open={editReminderModalOpen}
                  mode="edit"
                  reminder={editingReminder}
                  onSave={handleUpdateReminder}
                  onCancel={() => { setEditReminderModalOpen(false); setEditingReminder(null); }}
                />
              </Section>
            )}
            {selectedTab === 6 && patient && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <AgentConfigPanel userId={patient.userId} onConfigChange={setAgentConfig} />
              </Section>
            )}
            {selectedTab === 7 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <PatientInfoDetails patient={patient} />
              </Section>
            )}
          </div>
        </div>

      {/* Edit Patient Modal */}
      <EditPatientModal
        open={editPatientModalOpen}
        patient={patient}
        onSave={handleUpdatePatient}
        onCancel={() => setEditPatientModalOpen(false)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirmOpen}
        title="Confirmar Eliminación"
        message={
          <>
            ¿Estás seguro de que deseas desactivar a <strong>{patient?.firstName} {patient?.lastName}</strong>? 
            Esta acción desactivará el acceso del paciente al sistema.
          </>
        }
        confirmText="Sí, Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeletePatient}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordOpen}
        patientName={`${patient?.firstName || ''} ${patient?.lastName || ''}`.trim()}
        onSave={handleChangePassword}
        onCancel={() => setChangePasswordOpen(false)}
      />
    </div>
  );
};

export default PatientDetail; 